// Client side of the sprite library: resolve a keyword to a sprite through the bundled index
// and draw it from its sheet. The index is in the bundle (runtime/shared/sprite-index.ts), so a
// lookup never waits; only the sheet image loads lazily, and a draw before it arrives returns
// false so the game can fall back to shapes for a frame or two.

import type { Sprite, SpriteOptions } from "../contract.js";
import { searchSprites, packOf, type SpriteEntry } from "../shared/sprites.js";
import { spriteIndex } from "../shared/sprite-index.js";

const SHEETS_BASE = "/sprites/";

const images = new Map<string, HTMLImageElement>();
const resolved = new Map<string, Sprite | undefined>();

function sheet(file: string): HTMLImageElement {
  let img = images.get(file);
  if (!img) {
    img = new Image();
    img.src = SHEETS_BASE + file;
    images.set(file, img);
  }
  return img;
}

function make(e: SpriteEntry): Sprite {
  const pack = packOf(spriteIndex, e);
  const pixel = pack ? pack.style.startsWith("pixel") : false;
  const img = sheet(e.sheet);
  return {
    name: e.name,
    pack: e.pack,
    w: e.w,
    h: e.h,
    draw(ctx, x, y, w = e.w, h = e.h, angle = 0) {
      if (!img.complete || img.naturalWidth === 0) return false;
      ctx.save();
      ctx.translate(x, y);
      if (angle) ctx.rotate(angle);
      if (pixel) ctx.imageSmoothingEnabled = false;
      ctx.drawImage(img, e.x, e.y, e.w, e.h, -w / 2, -h / 2, w, h);
      ctx.restore();
      return true;
    },
  };
}

/**
 * The sprite for a keyword, or undefined when nothing in the index answers to it. Deterministic
 * (see searchSprites) and memoised, so a draw can call it every frame.
 */
export function spriteFor(keyword: string, opts: SpriteOptions = {}): Sprite | undefined {
  const key = `${keyword}|${opts.style ?? ""}|${opts.pack ?? ""}`;
  if (resolved.has(key)) return resolved.get(key);
  const [best] = searchSprites(spriteIndex, keyword, { style: opts.style, pack: opts.pack, limit: 1 });
  const s = best ? make(best) : undefined;
  resolved.set(key, s);
  return s;
}
